// @ts-nocheck
/*
 * Port of views/dashboard/callcenter-admin-alert.js.
 * API: API_CALL_CENTRE_ADMIN_ALERT (single GET). Byte-identical:
 *   - CALL_CENTRE_ADMIN only (returns null for other roles)
 *   - No auto-refresh interval (commented out in JS source — DO NOT re-enable)
 *   - Reads: data.has_alerts, .call_center_name, .alerts[], .channel_licence
 *     {available,used,valid}, .parent_companies[] {voice_balance_valid,
 *     tts_balance_valid}
 *   - Returns null while data === null AND !loading (avoids empty flash)
 * Sections: Header (Action Required / All Clear), alert list, 3 status
 * pills (Channel Licence, Credit Status, TTS Credit Status).
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { getApiCall } from '@/service/httpCall';
import { API_CALL_CENTRE_ADMIN_ALERT } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { Storage } from '@/utilities/setting';

export default function CallCenterAdminAlert() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isCallCenterAdmin, setIsCallCenterAdmin] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  useEffect(() => {
    try {
      const rawUser = Storage.getItem('user');
      if (!rawUser) return;
      const user = JSON.parse(rawUser);
      if (user?.role === USER_ROLES.CALL_CENTRE_ADMIN) {
        setIsCallCenterAdmin(true);
      }
    } catch (e) {}
  }, []);

  const fetchAlert = useCallback((showToast = false) => {
    setLoading(true);
    getApiCall(API_CALL_CENTRE_ADMIN_ALERT)
      .then((resp) => {
        setData(resp);
        setLastRefreshedAt(new Date());
        if (showToast) toast.success('Alerts refreshed');
      })
      .catch((err) => {
        if (showToast) toast.error(friendlyApiError(err) || 'Failed to refresh alerts');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isCallCenterAdmin) return;
    fetchAlert(false);
  }, [isCallCenterAdmin, fetchAlert]);

  if (!isCallCenterAdmin) return null;
  if (!data && !loading) return null;

  const hasAlerts = !!data?.has_alerts;
  const channelLicence = data?.channel_licence || {};
  const parentCompanies = data?.parent_companies || [];

  const borderColor = hasAlerts ? '#ffb703' : '#34d399';
  const voiceOK = parentCompanies.every((c) => c.voice_balance_valid);
  const ttsOK = parentCompanies.every((c) => c.tts_balance_valid);

  return (
    <div
      className={'mb-6 overflow-hidden rounded-2xl border-2 p-4 ' + (hasAlerts ? 'animate-pulse-slow' : '')}
      style={{
        borderColor,
        boxShadow: hasAlerts
          ? '0 10px 30px rgba(255,45,85,0.35)'
          : '0 6px 20px rgba(16,185,129,0.25)',
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {hasAlerts ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle2 className="h-5 w-5" />}
          <div>
            <div className="text-base font-extrabold tracking-wide">
              {hasAlerts ? 'Action Required' : 'All Clear'}
            </div>
            <div className="text-xs">
              Call Center: <strong>{data?.call_center_name || '—'}</strong>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchAlert(true)}
          disabled={loading}
          className={
            'inline-flex h-8 items-center gap-2 rounded-full border border-current px-3 text-xs font-bold ' +
            (loading ? 'cursor-not-allowed opacity-70' : 'cursor-pointer')
          }
          title="Reload Alerts"
        >
          <RefreshCw className={'h-3.5 w-3.5 ' + (loading ? 'animate-spin' : '')} />
          {loading ? 'Refreshing…' : 'Reload'}
        </button>
      </div>

      {Array.isArray(data?.alerts) && data.alerts.length > 0 && (
        <ul className="mt-3 list-disc pl-5 text-[13px] leading-normal">
          {data.alerts.map((msg, idx) => (
            <li key={idx} className="font-semibold">{msg}</li>
          ))}
        </ul>
      )}

      <div className="mt-3 grid gap-2.5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        {/* Channel Licence */}
        <div className="rounded-xl border border-current/40 p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="text-[11px] font-bold tracking-widest">CHANNEL LICENCE</div>
          <div className="mt-1 flex items-baseline gap-2">
            <div className="text-2xl font-extrabold">Available - {channelLicence.available ?? 0}</div>
            <span className="text-xs">Used: {channelLicence.used ?? 0}</span>
          </div>
          <div className="mt-1.5 inline-block rounded-full px-2 py-0.5 text-[11px] font-extrabold tracking-wider">
            {channelLicence.valid
              ? 'VALID'
              : Number(channelLicence.available) < 0
                ? 'OVER-SUBSCRIBED'
                : 'INVALID (=0)'}
          </div>
        </div>

        {/* Credit Status */}
        <div className="rounded-xl border border-current/40 p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="text-[11px] font-bold tracking-widest">CREDIT STATUS</div>
          <div className="mt-1.5 flex items-center gap-2">
            {voiceOK ? <CheckCircle2 className="h-[18px] w-[18px]" /> : <AlertTriangle className="h-[18px] w-[18px]" />}
            <div className="text-base font-extrabold">
              {voiceOK ? 'Valid Credits' : 'Credit Attention Required'}
            </div>
          </div>
          <div className="mt-2 text-xs opacity-80">
            {!voiceOK
              ? 'One or more underlying voice balances are low or exhausted. Call creation may fail.'
              : 'Voice balance status looks healthy.'}
          </div>
        </div>

        {/* TTS Credit Status */}
        <div className="rounded-xl border border-current/40 p-3 transition-[transform,box-shadow] duration-200 ease-out hover:-translate-y-0.5 hover:scale-[1.02]">
          <div className="text-[11px] font-bold tracking-widest">TTS CREDIT STATUS</div>
          <div className="mt-1.5 flex items-center gap-2">
            {ttsOK ? <CheckCircle2 className="h-[18px] w-[18px]" /> : <AlertTriangle className="h-[18px] w-[18px]" />}
            <div className="text-base font-extrabold">
              {ttsOK ? 'Valid Credits' : 'Credit Attention Required'}
            </div>
          </div>
          <div className="mt-2 text-xs opacity-80">
            {!ttsOK
              ? 'One or more underlying TTS balances are low or exhausted. TTS calls may fail.'
              : 'TTS balance status looks healthy.'}
          </div>
        </div>
      </div>
    </div>
  );
}
